import { obsMetricNames } from "./observations_metricUtils";

export const StatusValues = ["active", "suspended", "inactive"] as const;

export type Status = (typeof StatusValues)[number];
export const RoleValues = ["user", "admin"] as const;

export type NumberRange = { min: number; max: number };
export type BloodPressureRange = { systolic: number; diastolic: number };

export interface UserAccount {
  /** A uuid */
  id: string;
  /** The user's email address */
  email: string;
  /** The user's first name */
  firstName: string;
  /** The user's last name */
  lastName: string;
  /** The user's role */
  role: string;
  identity_id: string;
  initials?: string;
  shortName?: string;
}

export interface Location {
  id: string;
  status: Status;
  name: string;
  shortName?: string;
  area?: string;
  /** When missing or null, location has unlimited capacity. */
  capacity?: number | null;
}

export type PhotoType = "problem" | "patient";

export const EncounterStatus = [
  "draft",
  "submitted",
  "triaged",
  "seen",
  "discharged",
  "pendingDischarged",
  "admitted",
  "consult",
  "transferred",
  "disabled",
  "expected",
  "didnotpresent"
] as const;
export type EncounterStatus = (typeof EncounterStatus)[number];

// "linked" | "clerked"
export const TaskStatusValues = ["not_requested", "requested", "ready", "done"] as const;
export type TaskStatus = (typeof TaskStatusValues)[number];

// Centralized task state configuration
// This defines the allowed states for task definitions and their mapping to TaskStatus
export const TASK_STATE_OPTIONS = [
  { name: "Not Requested", status: "not_requested" as TaskStatus, colour: "#C3C8DE" },
  { name: "Requested", status: "requested" as TaskStatus, colour: "#FCD3B8" },
  { name: "Ready", status: "ready" as TaskStatus, colour: "#D97B66" },
  { name: "Done", status: "done" as TaskStatus, colour: "#B0E2D3" },
] as const;

// Helper to get TaskStatus from state name
export const getTaskStatusFromStateName = (stateName: string): TaskStatus => {
  const option = TASK_STATE_OPTIONS.find(o => o.name === stateName);
  return option?.status ?? "not_requested";
};

// Helper to get state name from TaskStatus
export const getStateNameFromTaskStatus = (status: TaskStatus): string => {
  const option = TASK_STATE_OPTIONS.find(o => o.status === status);
  return option?.name ?? "Not Requested";
};

export const TaskType = [
  "Medications",
  "Blood Test",
  "ECG",
  "Urine Test",
  "Pregnancy Test",
  "X-Ray",
  "Ultrasound",
  "CT Scan",
  "RAT Consult",
] as const;
export type TaskType = (typeof TaskType)[number];

export interface TaskHistory {
  toStatus: TaskType;
  fromStatus: TaskType;
  when: string;
  userId: string;
  userName: string;
  actingAsName?: string
}
export interface Task {
  type: TaskType;
  status: TaskStatus;
  history?: TaskHistory[];
}

export type HealthHistoryTypes =
  | "cardiac"
  | "respiratory"
  | "neurological"
  | "renal"
  | "hepatic"
  | "endocrine"
  | "psychiatric"
  | "musculoskeletal"
  | "hematological"
  | "immunological"
  | "biliary"
  | "intestinal"
  | "cancer"
  | "mens-health"
  | "womens-health"
  | "dermatological"
  | "other";

export const Sex = [
  "male",
  "female"
] as const;
export type Sex = (typeof Sex)[number];

export interface Patient {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  external?: any;
  // TODO which one of these are we using?
  sex?: Sex;
  gender?: string;
  pronouns?: string;
  dateOfBirth?: string;
  allergies?: Allergy[];
  noAllergies?: boolean;
  medicareNo?: string;
  // TODO confirm this is an actual property
  medicareNoPos?: string;
  umrn?: string;
  anonymous?: boolean;
  deanonymiseNote?: Pick<Note, "text">;
  unknown?: boolean;
  gpDetails?: {
    clinicName?: string;
    doctorFirstName?: string;
    doctorLastName?: string;
  };
  nextOfKin?: {
    firstName?: string;
    lastName?: string;
    relationship?: string;
  };
  previousNames?: Array<{
    firstName?: string;
    lastName?: string;
  }>;
}

export interface Photo {
  type: PhotoType;
  data: string;
}

export const Severity = ["anaphylaxis", "breathing", "swelling", "vomiting", "rash", "other"] as const;
export type Severity = (typeof Severity)[number];

export interface Allergy {
  description: string;
  severity?: Severity;
  source: "patient" | "practitioner";
}

export interface TimelineItemBase {
  id: string;
  text: string;
  createdBy: string;
  created: string;
  createdByName: string;
  createdByRole: string;
  // Auto generated by backend on creation
  lastModified: string;
  /** When the action was performed while acting as another clinician */
  actingAsName?: string;
  actingAsClinician?: string;
}

export type Activity = TimelineItemBase & {
  type?: "triage" | "tasks" | "allergy" | "location" | "observation" | "status";
  info?: Partial<Observation> & { id?: string };
};

export type Note = TimelineItemBase & {
  /** @deprecated Record role in title if required */
  role?: "nurse" | "doctor";
  type?: "communication";
  title?: string;
};

export interface Problem {
  patient?: string;
  triage?: string;
  nursingAssessment?: string;
  ambulance?: string;
  expects?: string;
}

export interface Practitioner {
  id: string;
  name: string;
  shortName: string;
  fullName: string;
  type: string;
}

export enum Pain {
  NO_PAIN = 0,
  VERY_MILD,
  DISCOMFORTING,
  TOLERABLE,
  DISTRESSING,
  VERY_DISTRESSING,
  INTENSE,
  VERY_INTENSE,
  HORRIBLE,
  UNBEARABLE,
  UNSPEAKABLE,
}

export enum Frailty {
  VERY_FIT = 1,
  WELL,
  MANAGING_WELL,
  VULNERABLE,
  MILDLY_FRAIL,
  MODERATELY_FRAIL,
  SEVERELY_FRAIL,
  VERY_SEVERELY_FRAIL,
  TERMINALLY_ILL,
  NOT_SURE = -1,
}

export enum ClinicalRisk {
  LOW,
  LOW_MEDIUM,
  MEDIUM,
  HIGH,
}

export enum InfectiousSymptom {
  FEVER = "fever",
  COUGH = "cough",
  SHORTNESS_OF_BREATH = "shortness of breath",
  RUNNY_NOSE = "runny nose",
  SORE_THROAT = "sore throat",
  DIARRHOEA = "diarrhoea",
  COVID = "covid",
  CHEMO = "chemo",
}

export type NEWS2Score = {
  score: number;
  risk: ClinicalRisk;
  incomplete: boolean;
};

type NEWS2ScoreCharted = {
  score: number;
  risk: number;
  incomplete: boolean;
};

export type FallRisk = {
  fallenInLastYear: boolean;
  feelUnsteady: boolean;
  worriedAboutFalling: boolean;
};

export interface PatientEncounter {
  id: string;
  created: string;
  status: EncounterStatus;
  patient: Patient;
  distressed?: boolean;
  lastDistress?: string;
  lastModified?: string;
  /** @deprecated */
  infectious?: string;
  infectiousSymptoms?: InfectiousSymptom[];
  problem: Problem;
  photos?: Photo[];
  pain?: Pain;
  frailty?: Frailty;
  arrived?: number;
  triaged?: number;
  triageNurse?: {
    firstName?: string;
    lastName?: string;
  };
  /**
   * When the status on MEV is changed to discharged. Less accurate than
   * `dischargedDateTime` however.
   */
  discharged?: number;
  /** The time that EDIS reports the patient as discharged */
  dischargeDateTime?: number;
  modeOfArrival?: string;
  atsScore?: number;
  location?: Location;
  notes?: Note[];
  obsNotRequired?: boolean;
  tasks?: Task[];
  practitioners?: Practitioner[];
  observations?: Observation[];
  nextObsDue?: number; // should be inserted by the server
  activities?: Activity[];
  /** Single clinician comment for prominent patient information */
  comment?: {
    text: string;
    severity?: 'low' | 'medium' | 'high';
    modifiedBy?: { id: string; name: string };
  } | null;
  /** Only exists when updating the encounter */
  // note?: { text: string };
  note?: Pick<Note, "text" | "title">;
  delegates: Array<{
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
    phone_number?: string;
    relationship?: string;
    canAct?: boolean;
  }>;
  riskScore?: NEWS2Score;
  delegatedData?: Partial<Patient>;
  fallRisk?: FallRisk;
  source?: "edis" | "delegate" | "delegate-mobile" | "patient" | "nurse"; // "delegate" for encounters created on portal with an accompanying person, "delegate-mobile" for encounters created via mobile app
  healthHistory?: {
    type?: HealthHistoryTypes;
    details?: string;
  }[];
  previousOperations?: {
    type: string;
    when?: "lt1month" | "lt6months" | "gt6months";
  }[];
  homeSupport?: "independent" | "aid" | "assist";
  homeStatus?:
  | "homeless"
  | "alone_at_home"
  | "with_partner"
  | "with_family"
  | "with_friends"
  | "in_residential_institution"
  | "in_retirement_community"
  | "in_nursing_home";
  alcoholConsumption?:
  | "none"
  | "less_than_one"
  | "one_to_two"
  | "two_to_five"
  | "five_to_ten"
  | "more_than_ten";
  tobaccoConsumption?:
  | "ex_smoker"
  | "occasional"
  | "little"
  | "moderate"
  | "heavy";
  recreationalDrugs?: "yes" | "no" | "decline";
  employmentStatus?:
  | "child"
  | "student"
  | "employed"
  | "unpaid_work"
  | "unable_to_work"
  | "retired";
  healthcareDirective: "yes" | "no";
  regularMeds: "moreThan3" | "yes" | "no";
  // Expects-related fields
  admittingSpecialty?: string;
  bedSlipSubmitted?: "yes" | "no";
  triageDirection?: string;
  patientOrigin?: string;
  referrerDetails?: {
    from?: string;
    contactName?: string;
    contactNumber?: string;
  };
}

export enum Consciousness {
  NA = "N",
  ALERT = "A",
  CONFUSED = "C",
  VERBAL = "V",
  PAINFUL = "P",
  UNRESP = "U",
}

export const Rhythm = [
  "Regular",
  "Irregular",
  "Paced",
  "SR - Sinus Rhythm",
  "A Fib - Atrial Fibrillation",
  "A Flut - Atrial Flutter",
  "1°HB - First-degree Heart Block",
  "2°HB - Second-degree Heart Block",
  "3°HB - Third-degree Heart Block",
  "SVT - Supraventricular Tachycardia",
  "VF - Ventricular Fibrillation",
  "VT - Ventricular Tachycardia",
] as const;


export type ObservationNeuroLimb = {
  colour?: "normal" | "mottled" | "dusky" | "pale" | "flushed";
  warmth?: "hot" | "warm" | "cool" | "cold";
  movement?: "normal" | "impaired" | "absent";
  sensation?: "normal" | "impaired" | "absent";
  capillaryRefill?: "normal" | "absent";
  pulse?: "normal" | "absent" | "weak" | "doppler";
};
export type ObservationNeuroMovementArm = "none" | "extension" | "spastic" | "severe" | "mild" | "normal";
export type ObservationNeuroMovementLeg = "none" | "extension" | "severe" | "mild" | "normal";


//# region Observation

export interface Observation extends TimelineItemBase {
  [obsMetricNames.consciousness]: Consciousness;
  [obsMetricNames.respirations]?: number;
  [obsMetricNames.o2Sats]?: number;
  [obsMetricNames.o2Flow]?: number;
  [obsMetricNames.hypercapnicRespiratoryFailure]?: boolean;
  [obsMetricNames.heartRate]?: number;
  [obsMetricNames.bloodPressure]?: BloodPressureRange;
  [obsMetricNames.temperature]?: number;
  [obsMetricNames.rhythm]?: (typeof Rhythm)[number];
  [obsMetricNames.bloodGlucose]?: number;
  [obsMetricNames.bloodAlcohol]?: number;
  [obsMetricNames.painScore]?: number;
  [obsMetricNames.riskScore]?: NEWS2ScoreCharted;
  note?: string;
  invalid?: boolean;
  bedside?: {
    bloodPressure?: {
      leftArm?: {
        systolic?: number;
        diastolic?: number;
      };
      rightArm?: {
        systolic?: number;
        diastolic?: number;
      };
      laying?: {
        systolic?: number;
        diastolic?: number;
      };
      standing?: {
        systolic?: number;
        diastolic?: number;
      };
    };
    heartRate?: string | number;
  };
  ventilator?: {
    tidalVolume?: number;
    minuteVentilation?: number;
    frequencyRate?: number;
    peakPressure?: number;
    endTidalCarbonDioxide?: number;
  };
  urinalysis?: {
    abnormalities?: string;
    distream?: string;
    pregnancy?: string;
    glucose?: string;
    protein?: string;
    blood?: string;
    bilirubin?: string;
    nitrite?: string;
    urobilinogen?: string;
    leukocytes?: string;
    ph?: string;
    ketone?: string;
    specificGravity?: string;
  };
  neuro?: {
    eyeOpening?: "C" | 1 | 2 | 3 | 4;
    verbal?: "T" | 1 | 2 | 3 | 4 | 5 | 6;
    motor?: 1 | 2 | 3 | 4 | 5 | 6;
    glasgowTotal?: number;
    pupil?: {
      leftSize?: number;
      rightSize?: number;
      leftReaction?: "C" | "+" | "-";
      rightReaction?: "C" | "+" | "-";
    };
    movement?: {
      armRight?: ObservationNeuroMovementArm;
      armLeft?: ObservationNeuroMovementArm;
      legRight?: ObservationNeuroMovementLeg;
      legLeft?: ObservationNeuroMovementLeg;
    }
    limbNeuro?: {
      rightArm?: ObservationNeuroLimb;
      leftArm?: ObservationNeuroLimb;
      rightLeg?: ObservationNeuroLimb;
      leftLeg?: ObservationNeuroLimb;
    };
  };
}

export const eyeOpeningLabels = {
  4: "Spontaneous",
  3: "To sound",
  2: "To pressure",
  1: "None",
  C: "Closed by swelling / injury",
};

export const verbalLabels = {
  5: "Oriented",
  4: "Confused",
  3: "Inappropriate words",
  2: "Incomprehensible sounds",
  1: "None",
  T: "Intubated / Tracheostomy",
};

export const motorLabels = {
  6: "Obey commands",
  5: "Localise to pressure",
  4: "Normal flexion",
  3: "Abnormal flexion",
  2: "Extension",
  1: "None",
};

//#

export const armMovementLabels: { [K in ObservationNeuroMovementArm]: string } = {
  normal: "Normal power",
  mild: "Mild weakness",
  severe: "Severe weakness",
  spastic: "Spastic flexion",
  extension: "Extension",
  none: "No response",
};
export const legMovementLabels: { [K in ObservationNeuroMovementLeg]: string } = {
  normal: "Normal power",
  mild: "Mild weakness",
  severe: "Severe weakness",
  extension: "Extension",
  none: "No response",
};
//#
export const limbNeuroColorLabels: { [K in NonNullable<ObservationNeuroLimb["colour"]>]: string } = {
  normal: "Normal",
  mottled: "Mottled",
  dusky: "Dusky",
  pale: "Pale",
  flushed: "Flushed",
};
export const limbNeuroWarmthLabels: { [K in NonNullable<ObservationNeuroLimb["warmth"]>]: string } = {
  hot: "Hot",
  warm: "Warm",
  cool: "Cool",
  cold: "Cold",
};
export const limbNeuroMovementLabels: { [K in NonNullable<ObservationNeuroLimb["movement"]>]: string } = {
  normal: "Normal",
  impaired: "Impaired",
  absent: "Absent",
};
export const limbNeuroSensationLabels: { [K in NonNullable<ObservationNeuroLimb["sensation"]>]: string } = {
  normal: "Normal",
  impaired: "Impaired",
  absent: "Absent",
};
export const limbNeuroCapillaryRefillLabels: { [K in NonNullable<ObservationNeuroLimb["capillaryRefill"]>]: string } = {
  normal: "Normal <= 2 secs",
  absent: "Abnormal > 2 secs",
};
export const limbNeuroPulseLabels: { [K in NonNullable<ObservationNeuroLimb["pulse"]>]: string } = {
  normal: "Normal",
  weak: "Weak",
  doppler: "On Doppler Only",
  absent: "Absent",
};

//# endregion

export type Notification = {
  id: string;
  encounterId: string;
  priority: "normal" | "high";
  template: "general";
  message: string;
};

export type AnalyticData = {
  num: number;
  /** Last 24 hrs */
  hourly: number[];
};

export type Analytics = {
  waittime: AnalyticData;
  patients: AnalyticData;
  triage: AnalyticData;
  distress: AnalyticData;
  expects: AnalyticData;
};

export enum Practitioners {
  SNR_DOCTOR = "snrDoctor",
  DOCTOR = "doctor",
  NURSE = "nurse",
}

export enum ActivityFilterSections {
  INPUT = "input",
  CATEGORY = "category",
  SYSTEM = "system",
}

export type ActivityFilters = {
  [ActivityFilterSections.INPUT]?: (Practitioners | "other" | "System")[];
  [ActivityFilterSections.CATEGORY]?: (
    | "allergy"
    | "task"
    | "location"
    | "triage"
    | "observation"
    | "communication"
    | "note"
    | "comment"
    | "create"
  )[];
  [ActivityFilterSections.SYSTEM]?: (
    | "encounterEvents"
    | "outboundMessages"
    | "inboundMessages"
  )[];
};
